// v4 (V4-D6 parity with set_model_rules): a video's model rules as a fold on the video screen. The rules are plain
// words ("Kling for action, Gen-4.5 for dialogue, the cheapest for b-roll"), saved through the same tool the Director
// uses, so it reads them back the same way. Under it, each scene's model and tags as the rules see them now.
import { useState } from "react";
import { tapTool } from "../actions";
import { foldSummary } from "../fold";
import { loadModelRules, sceneModelLine } from "../modelrules";
import { bump, toast, useRefresh } from "../store";
import type { Project } from "../types";
import { Fold } from "./Fold";
import { useLoad } from "./bits";

export function ModelRules({ p }: { p: Project }) {
  const tick = useRefresh("project");
  const ids = (p.script?.scenes ?? []).map((s) => s.id);
  const { data } = useLoad(() => loadModelRules(p.id, ids).catch(() => null), [p.id, tick, ids.join(",")]);
  const [text, setText] = useState("");
  const [series, setSeries] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!p.script) return null;
  const rules = [...(data?.video ?? []), ...(data?.series ?? []).map((r) => `${r} (series)`)];

  const save = async (words: string) => {
    setBusy(true);
    try {
      const r = await tapTool("set_model_rules", { project_id: p.id, rules: words, scope: series ? "series" : "video" }, words === "clear" ? "cleared the model rules" : `set the model rules: "${words}"`);
      if (!r) return;
      if (/couldn.t|which model/i.test(r.result)) return toast("Use model names and kinds of scene, like “Kling for action”.", "bad");
      setText("");
      bump("project");
      toast(words === "clear" ? "Rules cleared." : "Rules saved. They apply to clips not made yet.", "good");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Fold id="video.model-rules" title="Model rules" summary={foldSummary([rules.length ? rules.join("; ") : "None yet: every clip uses the video's model"])} className="mrules">
      {!!rules.length && (
        <ul className="mrules-list">
          {rules.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
      <form
        className="mrules-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) void save(text.trim());
        }}
      >
        <label className="field">
          <span className="field-label">Which video model for which scenes?</span>
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={400} placeholder="Kling for action, Gen-4.5 for dialogue" />
          <span className="field-hint">Kinds of scene: action, dialogue, close-up, product, landscape, b-roll, text. Or scene numbers.</span>
        </label>
        {data?.inSeries && (
          <label className="mrules-scope">
            <input type="checkbox" checked={series} onChange={(e) => setSeries(e.target.checked)} /> For the whole series
          </label>
        )}
        <div className="mrules-acts">
          <button type="submit" className="btn btn-primary btn-small" data-action="models.rules" disabled={busy || !text.trim()}>
            {busy ? "Saving" : "Save rules"}
          </button>
          {!!rules.length && (
            <button type="button" className="btn btn-quiet btn-small" data-action="models.rules" disabled={busy} onClick={() => void save("clear")}>
              Clear
            </button>
          )}
        </div>
      </form>
      {!!data?.scenes.length && (
        <ol className="mrules-scenes" aria-label="Each scene's model">
          {data.scenes.map((s) => (
            <li key={s.sceneId}>
              <span className="mrules-n">{s.n}</span>
              <span>{sceneModelLine(s)}</span>
              {s.source === "rule" && <span className="mrules-by">rule</span>}
            </li>
          ))}
        </ol>
      )}
    </Fold>
  );
}
